import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePolling } from '../../video/hooks/usePolling';
import { fetchStressOptions, fetchStressTests } from '../api';
import type { StressOptions, StressTest, StressTestCreatedResponse } from '../types';
import { isStressTerminal } from '../types';
import { StressTestForm } from './StressTestForm';
import { endpointLabel, formatDuration, formatTimestamp, statusClasses } from './format';

const POLL_INTERVAL_MS = 3000;

export function StressPage() {
  const navigate = useNavigate();
  const [options, setOptions] = useState<StressOptions | null>(null);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchStressOptions()
      .then((o) => {
        if (!cancelled) setOptions(o);
      })
      .catch((e) => {
        if (!cancelled) setOptionsError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { data: tests, loading, error, refresh } = usePolling<StressTest[]>(
    () => fetchStressTests(100),
    POLL_INTERVAL_MS,
  );

  const handleCreated = useCallback(
    (created: StressTestCreatedResponse) => {
      setShowForm(false);
      refresh();
      navigate(`/gen-api/stress/${created.id}`);
    },
    [navigate, refresh],
  );

  const running = (tests || []).filter((t) => !isStressTerminal(t.status));
  const history = (tests || []).filter((t) => isStressTerminal(t.status));

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Stress Tests</h1>
          <p className="text-sm text-slate-500 mt-1">
            Generate synthetic load across the platform: a lightning batch run from a scan CSV plus
            bursts of gen-api calls. Everything runs in dummy mode — the provider call is replaced
            by a sleep, while queueing, storage and the database are exercised for real.
          </p>
        </div>
        {options && (
          <button
            onClick={() => setShowForm((v) => !v)}
            className="shrink-0 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors"
          >
            {showForm ? 'Close' : 'New test'}
          </button>
        )}
      </div>

      {optionsError && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 text-danger px-4 py-3 text-sm mb-4">
          Could not load stress options: {optionsError}
        </div>
      )}

      {showForm && options && (
        <div className="mb-6">
          <StressTestForm options={options} onCreated={handleCreated} />
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 text-danger px-4 py-3 text-sm mb-4">
          Failed to load stress tests: {error}
        </div>
      )}

      {loading && !tests && <div className="text-sm text-slate-500">Loading stress tests…</div>}

      {tests && tests.length === 0 && !showForm && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-soft p-10 text-center">
          <p className="text-sm text-slate-600">No stress tests yet.</p>
          <button
            onClick={() => setShowForm(true)}
            className="mt-3 text-sm text-indigo-600 font-medium hover:text-indigo-700"
          >
            Run the first one
          </button>
        </div>
      )}

      {running.length > 0 && (
        <TestSection title="Running" tests={running} live />
      )}
      {history.length > 0 && <TestSection title="History" tests={history} />}
    </div>
  );
}

function TestSection({
  title,
  tests,
  live = false,
}: {
  title: string;
  tests: StressTest[];
  live?: boolean;
}) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-2">
        <h2 className="text-sm font-semibold text-slate-700">{title}</h2>
        {live && (
          <span className="flex items-center gap-1.5 text-xs text-indigo-600">
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
            live
          </span>
        )}
        <span className="text-xs text-slate-400">({tests.length})</span>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 shadow-soft overflow-hidden">
        <table className="data-table w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
            <tr>
              <th className="text-left font-medium px-4 py-2.5">Name</th>
              <th className="text-left font-medium px-4 py-2.5">Status</th>
              <th className="text-left font-medium px-4 py-2.5">Endpoint</th>
              <th className="text-right font-medium px-4 py-2.5">Calls</th>
              <th className="text-right font-medium px-4 py-2.5">Bursts</th>
              <th className="text-left font-medium px-4 py-2.5">Batch</th>
              <th className="text-right font-medium px-4 py-2.5">Duration</th>
              <th className="text-left font-medium px-4 py-2.5">Started</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {tests.map((test) => (
              <tr key={test.id} className="hover:bg-slate-50 transition-colors">
                <td data-label="Name" className="px-4 py-2.5">
                  <Link
                    to={`/gen-api/stress/${test.id}`}
                    className="text-indigo-600 font-medium hover:text-indigo-700"
                  >
                    {test.name || `Test ${test.id.slice(0, 8)}`}
                  </Link>
                </td>
                <td data-label="Status" className="px-4 py-2.5">
                  <span
                    className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusClasses(test.status)}`}
                  >
                    {test.status}
                  </span>
                </td>
                <td data-label="Endpoint" className="px-4 py-2.5 text-slate-600">
                  {endpointLabel(test.config?.model_type || '—')}
                </td>
                <td data-label="Calls" className="px-4 py-2.5 text-right text-slate-600 tabular-nums">
                  {test.calls_submitted}
                  <span className="text-slate-400"> / {test.calls_planned}</span>
                  {test.calls_failed_to_submit > 0 && (
                    <span className="text-danger" title="Rejected at submit time">
                      {' '}
                      ({test.calls_failed_to_submit} rejected)
                    </span>
                  )}
                </td>
                <td data-label="Bursts" className="px-4 py-2.5 text-right text-slate-600 tabular-nums">
                  {test.bursts_completed}
                </td>
                <td data-label="Batch" className="px-4 py-2.5 text-slate-500 text-xs">
                  {test.lightning_batch_id ? (
                    <span className="font-mono">{test.lightning_batch_id.slice(0, 8)}</span>
                  ) : test.lightning_error ? (
                    <span className="text-warning-700" title={test.lightning_error}>
                      skipped
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td data-label="Duration" className="px-4 py-2.5 text-right text-slate-600 tabular-nums">
                  {formatDuration(test.started_at, test.finished_at)}
                </td>
                <td data-label="Started" className="px-4 py-2.5 text-slate-500 text-xs">
                  {formatTimestamp(test.started_at || test.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
